/**
 * @module AdapterList
 * @package @geenius/adapters/solidjs-kobalte
 * @description Searchable Kobalte-backed adapter grid for the SolidJS library
 * variant.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import { createAdapters } from "@geenius/adapters/solidjs";
import type { JSX } from "solid-js";
import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { cn, loadKobalteCore } from "../lib/kobalte";
import { AdapterCard } from "./AdapterCard";

export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  filterStatus?: AdapterStatusType;
  class?: string;
}

export function AdapterList(props: AdapterListProps): JSX.Element {
  const ctx = createAdapters();
  const [core] = createResource(loadKobalteCore);
  const TextField = createMemo(() => core()?.TextField);
  const [search, setSearch] = createSignal("");

  const filtered = createMemo(() => {
    let domains = [...ADAPTER_DOMAINS];
    const query = search().toLowerCase();

    if (query) {
      domains = domains.filter((domain) => {
        return (
          (DOMAIN_LABELS[domain] ?? domain).toLowerCase().includes(query) ||
          domain.includes(query) ||
          (ctx.statuses[domain]?.provider?.toLowerCase().includes(query) ??
            false)
        );
      });
    }

    if (props.filterStatus) {
      domains = domains.filter(
        (domain) => ctx.statuses[domain]?.status === props.filterStatus,
      );
    }

    return domains;
  });

  const inputClass =
    "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20";

  return (
    <Show
      fallback={
        <div
          aria-label="Loading adapters"
          class={cn("grid gap-3", props.class)}
          role="status"
        >
          <For each={[1, 2, 3, 4]}>
            {() => (
              <div class="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div class="flex items-start gap-4">
                  <div class="h-12 w-12 rounded-md border border-slate-200 bg-slate-100" />
                  <div class="flex-1 space-y-3 pt-1">
                    <div class="h-3 w-24 rounded-md bg-slate-100" />
                    <div class="h-2 w-48 rounded-md bg-slate-100" />
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
      }
      when={!ctx.isLoading}
    >
      <div
        class={props.class}
        data-geenius-component="AdapterList"
        data-geenius-variant="solidjs-kobalte"
      >
        <div class="mb-4">
          <Show
            fallback={
              <input
                aria-label="Search adapters"
                class={inputClass}
                onInput={(event) => setSearch(event.currentTarget.value)}
                placeholder="Search adapters..."
                type="text"
                value={search()}
              />
            }
            keyed
            when={TextField()}
          >
            {(KobalteTextField) => (
              <KobalteTextField.Root onChange={setSearch} value={search()}>
                <KobalteTextField.Input
                  aria-label="Search adapters"
                  class={inputClass}
                  onInput={(event) => setSearch(event.currentTarget.value)}
                  placeholder="Search adapters..."
                  type="text"
                />
              </KobalteTextField.Root>
            )}
          </Show>
        </div>

        <Show
          fallback={
            <div class="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
              <p class="text-sm font-medium text-slate-500">
                {search()
                  ? "No adapters match your search"
                  : "No adapters configured"}
              </p>
            </div>
          }
          when={filtered().length > 0}
        >
          <ul
            aria-label="Adapter list"
            class="m-0 grid list-none gap-3 p-0 sm:grid-cols-2"
          >
            <For each={filtered()}>
              {(domain) => (
                <li>
                  <AdapterCard
                    domain={domain}
                    onClick={
                      props.onSelect
                        ? () => props.onSelect?.(domain)
                        : undefined
                    }
                    status={ctx.statuses[domain]}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </Show>
  );
}
